<script lang="ts" setup>
import { computed } from 'vue';
import { CdrText, CdrLink, IconCaretLeft } from '@rei/cedar';
import type { ReturnsLeadProps } from '@returns-page/types/index.mts';

const props = defineProps<ReturnsLeadProps>();

const leadHeading = computed(() => props.text?.heading);
const leadBody = computed(() => props.text?.description);
const leadMedia = computed(() => ({
  src: 'https://www.rei.com/dam/18292224_returns-lead.svg',
  alt: props.text?.heading,
}));
</script>

<template>
  <div
    class="returns-lead"
    data-ui="returns-lead-component"
  >
    <div class="returns-lead__content">
      <cdr-link
        class="returns-lead__help-link"
        aria-label="Help Center Home"
        href="/help"
        modifier="standalone"
      >
        <icon-caret-left
          modifier="standalone"
          inherit-color
        />
        Help Center Home
      </cdr-link>
      <div class="returns-lead__content__inner">
        <div class="returns-lead__content__inner__image">
          <img
            class="returns-lead__content--img"
            data-ui="returns-lead-image"
            :src="leadMedia.src"
            :alt="leadMedia.alt"
          >
        </div>
        <div class="returns-lead__content__inner__lead-text">
          <div class="returns-lead__content--copy">
            <cdr-text
              class="returns-lead__heading"
              data-ui="returns-lead-heading"
              tag="h1"
            >
              {{ leadHeading }}
            </cdr-text>
            <cdr-text data-ui="returns-lead-description">
              {{ leadBody }}
            </cdr-text>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';

.returns-lead {
  max-height: fit-content;
  background-color: $cdr-color-background-secondary;

  &__heading {
    @include cdr-text-heading-serif-1000;

    @include cdr-lg-mq-down {
      @include cdr-text-heading-serif-800;
    }
  }

  &__help-link {
    display: flex;
    width: fit-content;
    padding: $cdr-space-one-x;
  }

  &__content {
    @include cdr-container;

    padding-bottom: $cdr-space-two-x;
    text-align: center;

    @include cdr-sm-mq-up {
      text-align: left;
    }

    &__inner {
      display: grid;
      gap: $cdr-space-one-x $cdr-space-one-x;

      @include cdr-sm-mq-up {
        grid-template-columns: 1fr 1fr;
        gap: $cdr-space-two-x $cdr-space-two-x;
      }

      &__lead-text {
        align-self: center;
      }
    }

    &--img {
      max-height: 330px;

      @include cdr-xs-mq-only {
        max-height: 135px;
      }
    }

    &--copy {
      @include cdr-sm-mq-up {
        max-width: 400px;
      }

      & h1 {
        @include cdr-text-heading-serif-800;

        margin-bottom: $cdr-space-three-quarter-x;
      }

      & p {
        @include cdr-text-body-300;
      }
    }
  }
}
</style>
